import {
    getCommunities,
    getEnrichedChaperee,
} from "../services/chaperonConnector"
import { consoleLog, buildFullName, safetyPercentage } from "./helpers"
import {
    COMMUNITY_MAP_NEARBY_KM,
    LOCATION_STALE_THRESHOLD_MS,
    SCORE_THRESHOLD_MEDIUM,
} from "../const/numbers"
import { distanceKm } from "./resolvedPlaceCache"
import {
    LOCALSTORAGE_COMMUNITIES,
    LOCALSTORAGE_COMMUNITIES_FETCHED_AT,
} from "../const/storage"

/**
 * Normalizes an enriched chaperee for the UI, mirroring the Android app's
 * CommunityRepository.toMember. Idempotent, so it is safe to re-apply to
 * data already cached in localStorage.
 *
 * For persons the API's latitude/longitude/overall_safety describe the place
 * entity joined via chaperee.slug (null when that slug was never resolved),
 * not the person. Their real position is location_latitude/location_longitude
 * and their risk is personal_score (same raw 0-20 scale as overall_safety).
 */
export const normalizeChaperee = (chaperee) => {
    if (!chaperee || chaperee.type !== "person") return chaperee

    return {
        ...chaperee,
        latitude: chaperee.location_latitude ?? null,
        longitude: chaperee.location_longitude ?? null,
        slug: chaperee.entity_slug ?? chaperee.slug,
        overall_safety: chaperee.personal_score ?? null,
        full_name: buildFullName(chaperee.first_name, chaperee.last_name),
    }
}

/**
 * Merges a chaperee reference (role/pin/threshold/order) with its enriched
 * API data and normalizes the result.
 */
export const mergeEnrichedChaperee = (reference, enrichedData) =>
    normalizeChaperee({
        ...enrichedData,
        role: reference.role,
        pinned: reference.pinned,
        status: reference.status,
        threshold: reference.threshold,
        order: reference.order !== undefined ? reference.order : null,
    })

/**
 * Fetches and enriches community data from localStorage or API
 * @param {string} userId - The user ID to fetch communities for
 * @returns {Promise<Array>} Array of communities with enriched place data
 */
export const fetchCommunityPlaces = async (userId) => {
    let communitiesData = localStorage.getItem("chaperon_user_communities")
    let shouldFetchFromAPI = !communitiesData

    // If not in localStorage, fetch from API
    if (shouldFetchFromAPI) {
        try {
            const response = await getCommunities(userId)
            if (response && response.communities) {
                // Enrich chaperee data using the new enriched endpoint
                for (const community of response.communities) {
                    if (community.chaperees) {
                        const chapereeReferences =
                            typeof community.chaperees === "string"
                                ? JSON.parse(community.chaperees)
                                : community.chaperees

                        if (Array.isArray(chapereeReferences)) {
                            // Fetch enriched data for each chaperee reference
                            const enrichedChaperees = []

                            for (const reference of chapereeReferences) {
                                try {
                                    // Fetch enriched data from new endpoint
                                    const enrichedData =
                                        await getEnrichedChaperee(reference.id)

                                    if (enrichedData) {
                                        // Merge reference properties with enriched data
                                        const enrichedChaperee =
                                            mergeEnrichedChaperee(
                                                reference,
                                                enrichedData,
                                            )
                                        enrichedChaperees.push(enrichedChaperee)
                                        consoleLog(
                                            `Enriched chaperee ${reference.id}:`,
                                            enrichedChaperee,
                                        )
                                    }
                                } catch (enrichError) {
                                    console.error(
                                        `Error fetching enriched data for chaperee ${reference.id}:`,
                                        enrichError,
                                    )
                                    // Skip chaperees that fail to enrich
                                    consoleLog(
                                        `Skipping chaperee ${reference.id} due to enrichment error`,
                                    )
                                }
                            }

                            // Update community with enriched chaperees
                            community.chaperees =
                                JSON.stringify(enrichedChaperees)
                        }
                    }
                }

                localStorage.setItem(
                    "chaperon_user_communities",
                    JSON.stringify(response),
                )
                localStorage.setItem(
                    LOCALSTORAGE_COMMUNITIES_FETCHED_AT,
                    String(Date.now()),
                )
                communitiesData = JSON.stringify(response)
            } else {
                // No communities found from API, return empty array
                consoleLog("No communities found from API")
                return []
            }
        } catch (error) {
            console.error("Error fetching communities from API:", error)
            // If API fails but we have localStorage data, try to use that instead
            communitiesData = localStorage.getItem("chaperon_user_communities")
            if (!communitiesData) {
                consoleLog("No fallback data available in localStorage")
                return []
            }
            consoleLog("Falling back to localStorage data after API error")
        }
    }

    // Parse and process community data (whether from localStorage or API)
    if (communitiesData) {
        try {
            const response = JSON.parse(communitiesData)
            const communitiesWithPlaces = []

            if (response.communities && Array.isArray(response.communities)) {
                response.communities.forEach((community) => {
                    let communityPlaces = []
                    let communityPersons = []

                    if (community.chaperees) {
                        try {
                            const parsedChaperees =
                                typeof community.chaperees === "string"
                                    ? JSON.parse(community.chaperees)
                                    : community.chaperees

                            if (Array.isArray(parsedChaperees)) {
                                const chaperees =
                                    parsedChaperees.map(normalizeChaperee)

                                // Helper function to sort chaperees by order property
                                const sortByOrder = (a, b) => {
                                    // Sort pinned places first (only for places)
                                    if (
                                        a.type === "place" &&
                                        b.type === "place"
                                    ) {
                                        if (a.pinned && !b.pinned) return -1
                                        if (!a.pinned && b.pinned) return 1
                                    }

                                    // Primary sort: by order property (ascending)
                                    const orderA =
                                        a.order !== undefined &&
                                        a.order !== null
                                            ? a.order
                                            : Number.MAX_SAFE_INTEGER
                                    const orderB =
                                        b.order !== undefined &&
                                        b.order !== null
                                            ? b.order
                                            : Number.MAX_SAFE_INTEGER

                                    if (orderA !== orderB) {
                                        return orderA - orderB
                                    }

                                    // Secondary sort: by updated_at if available
                                    if (a.updated_at && b.updated_at) {
                                        return (
                                            new Date(a.updated_at) -
                                            new Date(b.updated_at)
                                        )
                                    }

                                    // Tertiary sort: by id
                                    return a.id - b.id
                                }

                                communityPlaces = chaperees
                                    .filter(
                                        (chaperee) => chaperee.type === "place",
                                    )
                                    .sort(sortByOrder)

                                communityPersons = chaperees
                                    .filter(
                                        (chaperee) =>
                                            chaperee.type === "person",
                                    )
                                    .sort(sortByOrder)
                            }
                        } catch (parseError) {
                            console.error(
                                "Error parsing chaperees:",
                                parseError,
                            )
                        }
                    }

                    // Always include the community, even if it has no members yet
                    communitiesWithPlaces.push({
                        id: community.id,
                        name: community.name,
                        places: communityPlaces,
                        persons: communityPersons,
                        chiefs: community.chiefs,
                    })
                })
            }

            consoleLog(
                "Fetched and enriched communities with places:",
                communitiesWithPlaces,
            )
            return communitiesWithPlaces
        } catch (error) {
            console.error("Error parsing community data:", error)
            return []
        }
    }

    return []
}

/**
 * Like fetchCommunityPlaces, but a cached copy older than maxAgeMs is refetched
 * first (a cache with no recorded age counts as stale). For screens that show
 * community data without polling it themselves, so it can't sit stale for
 * days. If the refetch fails the cached copy is restored and served.
 * @param {string} userId - The user ID to fetch communities for
 * @param {number} maxAgeMs - How old a cached copy may be
 * @returns {Promise<Array>} Communities with `persons` and `places`
 */
export const fetchCommunityPlacesFresh = async (userId, maxAgeMs) => {
    const cached = localStorage.getItem(LOCALSTORAGE_COMMUNITIES)
    const fetchedAt = Number(
        localStorage.getItem(LOCALSTORAGE_COMMUNITIES_FETCHED_AT),
    )
    const isFresh =
        Number.isFinite(fetchedAt) &&
        fetchedAt > 0 &&
        Date.now() - fetchedAt < maxAgeMs

    if (!cached || isFresh) return fetchCommunityPlaces(userId)

    // fetchCommunityPlaces only hits the API when the cache key is absent, and
    // only re-creates the key on a successful fetch.
    localStorage.removeItem(LOCALSTORAGE_COMMUNITIES)
    const fresh = await fetchCommunityPlaces(userId)
    if (localStorage.getItem(LOCALSTORAGE_COMMUNITIES) === null) {
        localStorage.setItem(LOCALSTORAGE_COMMUNITIES, cached)
        return fetchCommunityPlaces(userId)
    }
    return fresh
}

/**
 * True once a person's last real location update is older than
 * LOCATION_STALE_THRESHOLD_MS. A person who has never posted a location has no
 * epoch and is not "stale" (the sidebar already says "No location data").
 * Mirrors the Android app's isLocationStale.
 */
export const isLocationStale = (person) =>
    person?.type === "person" &&
    person.location_updated_at_epoch != null &&
    Date.now() - Number(person.location_updated_at_epoch) * 1000 >
        LOCATION_STALE_THRESHOLD_MS

/**
 * Whether a map marker (a community person or place, a spotlight place or
 * the current place) matches the Community map's filter term: a
 * case-insensitive substring of its name, username, current place or
 * country. A blank term matches everything.
 */
export const markerMatchesFilter = (item, term) => {
    const needle = (term || "").trim().toLowerCase()
    if (!needle) return true
    return [
        item?.full_name,
        item?.username,
        item?.name,
        item?.fromGoogle?.name,
        item?.entity_name,
        item?.country,
    ].some(
        (value) =>
            typeof value === "string" && value.toLowerCase().includes(needle),
    )
}

/**
 * A member below its own alert threshold (or the medium band when it has
 * none). The same rule as the Members sidebar's alert badge. A raw score of
 * 0 is the most dangerous reading, so only a missing score is excluded.
 */
export const isAtRisk = (item) =>
    item?.overall_safety != null &&
    Number(safetyPercentage(item.overall_safety)) <
        (item.threshold ?? SCORE_THRESHOLD_MEDIUM)

/**
 * Whether a map marker passes the Community map's filter:
 * - { type: "text", term }: markerMatchesFilter
 * - { type: "atRisk" }: isAtRisk
 * - { type: "watchlist" }: pinned members
 * - { type: "nearby" }: within COMMUNITY_MAP_NEARBY_KM of `origin`
 *   ({ latitude, longitude }); nothing matches without an origin
 * A null filter matches everything.
 */
export const markerMatchesMapFilter = (item, filter, origin) => {
    switch (filter?.type) {
        case undefined:
            return true
        case "text":
            return markerMatchesFilter(item, filter.term)
        case "atRisk":
            return isAtRisk(item)
        case "watchlist":
            return Boolean(item?.pinned)
        case "nearby": {
            const latitude = parseFloat(item?.latitude)
            const longitude = parseFloat(item?.longitude)
            if (!origin || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return false
            }
            return distanceKm(origin, { latitude, longitude }) <= COMMUNITY_MAP_NEARBY_KM
        }
        default:
            return true
    }
}
